import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './carrier-packages.module.css';
import { messages } from './messages';
import { emptyArray } from 'ui-components/default-props/empty';
import { getCarriersPath } from '../../../../routes/constants';
import Global from '../../../../store/global-selectors';
import { SELECT_ALL } from '../constants';
import packagesSettingsStyle from '../packages-settings.module.css';
import { CarrierPackagesTable } from './carrier-packages-table';
import { useEnableSupplyPage } from '../../../../feature-flags/hooks/use-enable-supply-page';
import { SuppliesCardContainer } from '../../../home-page/version2/supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from '../../../home-page/version2/supplies-cards/supplies-page-constants';

export const CarrierPackages = ({ carrierData, selectedCarrierId, setSelectedCarrierId, selectedIds, setSelectedIds }) => {
  const enableSupplyPage = useEnableSupplyPage();

  const packages = useSelector(Global.selectSellerPackages);

  const selectedOption = useMemo(
    () => carrierData.find((c) => c.carrierId === selectedCarrierId) ?? carrierData[0],
    [carrierData, selectedCarrierId],
  );
  const [packagesIds, setPackagesIds] = useAsyncSafeState(emptyArray);
  const filteredPackages = useMemo(
    () => (selectedOption ? packages.filter((p) => p.carrierId === selectedOption.carrierId) : emptyArray),
    [packages, selectedOption],
  );

  const tableData = useMemo(
    () =>
      filteredPackages.map((p) => ({
        packageTypeId: p.packageTypeId,
        name: p.name,
        isSelected: selectedIds.includes(p.packageTypeId),
      })),
    [filteredPackages, selectedIds],
  );

  useEffect(() => {
    const selected = filteredPackages.reduce((result, p) => (!p.isHidden ? [...result, p.packageTypeId] : result), []);
    setSelectedIds(selected);
    setPackagesIds(filteredPackages.map((p) => p.packageTypeId));
  }, [filteredPackages, setPackagesIds, setSelectedIds]);

  const handleSelectionChange = useCallback(
    (key, isSelected) => {
      if (key === SELECT_ALL) {
        setSelectedIds(!isSelected ? [...packagesIds] : emptyArray);
        return;
      }
      setSelectedIds(!isSelected ? [...selectedIds, key] : selectedIds.filter((selectedId) => selectedId !== key));
    },
    [setSelectedIds, packagesIds, selectedIds],
  );

  return (
    <>
      <div className={packagesSettingsStyle['tab-description']}>
        <p>
          <FormattedMessage {...messages.description} />
        </p>
      </div>
      {carrierData.length ? (
        <>
          <p className={style['menu-label']}>
            <FormattedMessage {...messages['menu-label']} />
          </p>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton>
            <DropdownMenuToggler className={style['menu-toggler']} theme='white'>
              {selectedOption.name}
            </DropdownMenuToggler>
            {carrierData.map((carrier) => {
              const { name } = carrier;
              return (
                <DropdownMenuItem
                  key={name}
                  onClick={() => setSelectedCarrierId(carrier.carrierId)}
                  selected={selectedOption.name === name}
                >
                  {name}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenu>
        </>
      ) : (
        <BannerMessageV2
          content={
            <FormattedMessage
              {...messages.noCarriersBannerMessage}
              values={{
                link: (chunks) => <Link to={getCarriersPath()}>{chunks}</Link>,
              }}
            />
          }
          status={BannerStatus.Info}
        />
      )}

      {carrierData.length > 0 && <CarrierPackagesTable data={tableData} onSelectionChange={handleSelectionChange} />}
      {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.SETTINGS_PACKAGES} />}
    </>
  );
};
